import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Calendar as CalIcon, Clock, X, Users } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

const TIME_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"];

/**
 * Group-booking edit modal. Updates date/time/notes for ALL members at once
 * via PATCH /api/group-bookings/{group_id}.
 *
 * Props:
 *  - groupId:     the group_id being edited (required)
 *  - members:     array of booking rows in this group (used to prefill date/time/notes)
 *  - open:        controlled open state
 *  - onClose:     close callback
 *  - onSaved:     callback({ updated, members }) after a successful PATCH
 *  - isAdmin:     admins can skip the 1-hour lead-time guard
 */
export default function EditGroupBookingModal({ groupId, members = [], open, onClose, onSaved, isAdmin = false }) {
    const { settings } = useSettings();
    const leadMinutes = Math.max(0, parseInt(settings.min_booking_lead_minutes ?? 60, 10) || 0);
    const leader = useMemo(() => members.find((m) => m.is_group_initiator) || members[0] || {}, [members]);

    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!open) return;
        setDate(leader.date || "");
        setTime(leader.time || "");
        setNotes(leader.notes || "");
    }, [open, leader]);

    const todayLocal = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }, [now]);

    const availableSlots = useMemo(() => {
        if (date !== todayLocal) return TIME_SLOTS;
        const cutoff = now.getHours() * 60 + now.getMinutes() + (isAdmin ? 0 : leadMinutes);
        return TIME_SLOTS.filter((slot) => {
            const [h, m] = slot.split(":").map(Number);
            return h * 60 + m >= cutoff;
        });
    }, [date, todayLocal, leadMinutes, now, isAdmin]);
    const visibleTime = availableSlots.includes(time) ? time : "";
    useEffect(() => { if (time && !availableSlots.includes(time)) setTime(""); }, [date, leadMinutes, time, availableSlots]);

    const leadLabel = useMemo(() => {
        if (leadMinutes <= 0) return null;
        const h = Math.floor(leadMinutes / 60);
        const m = leadMinutes % 60;
        if (h && m) return `${h}h ${m}m`;
        if (h) return `${h} hour${h !== 1 ? "s" : ""}`;
        return `${m} minute${m !== 1 ? "s" : ""}`;
    }, [leadMinutes]);

    const save = async () => {
        if (!date || !time) { toast.error("Pick a date and time"); return; }
        if (date === todayLocal && !availableSlots.includes(time)) {
            toast.error(leadLabel ? `Bookings need at least ${leadLabel} lead time — please pick a later slot` : "That time has passed — please pick a later slot");
            return;
        }
        const payload = {};
        if (date !== (leader.date || "")) payload.date = date;
        if (time !== (leader.time || "")) payload.time = time;
        if ((notes || "") !== (leader.notes || "")) payload.notes = notes;
        if (Object.keys(payload).length === 0) { toast("Nothing to update"); return; }
        setSaving(true);
        try {
            const { data } = await api.patch(`/group-bookings/${groupId}`, payload);
            toast.success(`Updated ${data.updated || 0} of ${data.members || members.length} group members`);
            onSaved?.({ ...data, payload });
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Could not update group booking"));
        } finally {
            setSaving(false);
        }
    };

    if (!open || !groupId) return null;

    return (
        <div className="fixed inset-0 z-[60] bg-[#3D0C1F]/45 backdrop-blur-sm backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-group-modal">
            <div className="bg-[#FBF6F0] border border-[#C29A5B]/40 max-w-xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#6B1535]/15 sticky top-0 bg-[#FBF6F0] z-10">
                    <div>
                        <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] flex items-center gap-2"><Users size={11}/> Edit group booking</div>
                        <div className="font-serif-display text-xl text-[#3D0C1F] mt-0.5">{members.length} member{members.length !== 1 ? "s" : ""}</div>
                    </div>
                    <button onClick={onClose} className="text-[#7A5262] hover:text-[#3D0C1F] p-2" data-testid="edit-group-close" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-5">
                    <p className="text-xs text-[#7A5262]">Changes here apply to every member of this group in one go. Per-member services can be edited by opening each member&apos;s booking individually.</p>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] flex items-center gap-2"><CalIcon size={12}/> Date</span>
                            <input type="date" min={isAdmin ? undefined : todayLocal} value={date} onChange={(e) => setDate(e.target.value)}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="edit-group-date" />
                        </label>
                        <label className="block">
                            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] flex items-center gap-2"><Clock size={12}/> Time</span>
                            <select value={visibleTime} onChange={(e) => setTime(e.target.value)}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="edit-group-time">
                                <option value="">Select time</option>
                                {availableSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </label>
                    </div>
                    {!isAdmin && date === todayLocal && availableSlots.length > 0 && leadLabel && (
                        <p className="text-[0.65rem] text-[#7A5262]">Same-day slots need at least {leadLabel} lead time.</p>
                    )}

                    <label className="block">
                        <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Group notes</span>
                        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                            placeholder="Any preferences for the whole group?"
                            className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                            data-testid="edit-group-notes" />
                    </label>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#6B1535]/15 bg-[#FBF6F0] sticky bottom-0">
                    <button onClick={onClose} className="px-4 py-2 text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#3D0C1F]" data-testid="edit-group-cancel">Cancel</button>
                    <button onClick={save} disabled={saving}
                        className="btn-gold disabled:opacity-50 flex items-center gap-2"
                        data-testid="edit-group-save">
                        {saving && <Loader2 size={14} className="animate-spin"/>} Save changes
                    </button>
                </div>
            </div>
        </div>
    );
}
